import React, { useState, useRef } from 'react';
import axios from 'axios';
import Papa from 'papaparse';
import { toast } from 'sonner';
import { DatabaseZap, Plus, Trash2, CheckCircle2, XCircle, Upload, FileDown, FileUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  'Electronics + Devices', 'Home + Lifestyle', 'Antiques + Collectibles', 'Auto Parts + Accessories',
  'Fashion + Luxury', 'Health + Fitness', 'Tools + Machinery', 'Camping + Outdoors'
];

const STAGES = [
  { value: 'application_submitted', label: '1. Application Submitted' },
  { value: 'under_vetting', label: '2. Under Vetting' },
  { value: 'awaiting_dropoff', label: '3. Awaiting Drop-off' },
  { value: 'agreement_sent', label: '4. Agreement Signed' },
  { value: 'awaiting_checkin', label: '5. Awaiting Check-in' },
  { value: 'in_preparation', label: '6. In Preparation (creates item)' },
  { value: 'live_on_marketplace', label: '9. Live on Marketplace (creates item)' },
  { value: 'sold', label: '11. Sold (creates item + payout)' },
  { value: 'payout_issued', label: '12. Payout Issued (creates item + payout)' },
];

const emptyRow = () => ({
  _rid: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `r-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  seller_name: '', seller_phone: '', seller_email: '',
  item_title: '', category: 'Electronics + Devices', stage: 'live_on_marketplace',
  nominated_sale_price: '', sale_price: '', marketplace_id: '',
});

const CSV_HEADERS = ['seller_name', 'seller_phone', 'seller_email', 'item_title', 'category', 'stage', 'nominated_sale_price', 'sale_price', 'marketplace_id'];

export default function DataReentry() {
  const [rows, setRows] = useState([emptyRow()]);
  const [results, setResults] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const downloadTemplate = () => {
    const csv = CSV_HEADERS.join(',') + '\nJohn Smith,0400123456,john@example.com,World Cup Ice Bucket,Antiques + Collectibles,live_on_marketplace,500,,2078131859752986\nJane Doe,0411222333,,Holden Ute Tray,Auto Parts + Accessories,sold,1200,1100,2518323565288456\n';
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'listers-reentry-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const matchCategory = (v) => CATEGORIES.find(c => c.toLowerCase() === (v || '').trim().toLowerCase()) || (v || '').trim() || 'Electronics + Devices';
  const matchStage = (v) => STAGES.find(s => s.value === (v || '').trim().toLowerCase())?.value || 'application_submitted';

  const handleCsvUpload = (file) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (h) => h.trim().toLowerCase().replace(/\s+/g, '_'),
      complete: (res) => {
        if (!res.data.length) { toast.error('No rows found in that CSV'); return; }
        const missing = ['seller_name', 'item_title'].filter(h => !(h in res.data[0]));
        if (missing.length) { toast.error(`CSV is missing required columns: ${missing.join(', ')} — use the template`); return; }
        const parsed = res.data.map(r => ({
          ...emptyRow(),
          seller_name: (r.seller_name || '').trim(),
          seller_phone: (r.seller_phone || '').trim(),
          seller_email: (r.seller_email || '').trim(),
          item_title: (r.item_title || '').trim(),
          category: matchCategory(r.category),
          stage: matchStage(r.stage),
          nominated_sale_price: (r.nominated_sale_price || '').toString().replace(/[$,]/g, '').trim(),
          sale_price: (r.sale_price || '').toString().replace(/[$,]/g, '').trim(),
          marketplace_id: (r.marketplace_id || '').trim(),
        }));
        setRows(parsed);
        setResults(null);
        toast.success(`Loaded ${parsed.length} row${parsed.length === 1 ? '' : 's'} from CSV — review below, then import`);
      },
      error: () => toast.error('Could not read that file — make sure it is a CSV'),
    });
  };

  const setField = (idx, field, value) => {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  const validate = (r) => {
    if (!r.seller_name.trim()) return 'Seller name required';
    if (!r.item_title.trim()) return 'Item title required';
    if (!r.nominated_sale_price || isNaN(parseFloat(r.nominated_sale_price))) return 'Nominated price required';
    if (['sold', 'payout_issued'].includes(r.stage) && (!r.sale_price || isNaN(parseFloat(r.sale_price)))) return 'Sale price required for sold rows';
    return null;
  };

  const submit = async () => {
    const errors = rows.map(validate);
    if (errors.some(Boolean)) {
      toast.error(errors.find(Boolean));
      return;
    }
    try {
      setSubmitting(true);
      setResults(null);
      const payload = {
        rows: rows.map(r => ({
          seller_name: r.seller_name.trim(),
          seller_phone: r.seller_phone.trim() || null,
          seller_email: r.seller_email.trim() || null,
          item_title: r.item_title.trim(),
          category: r.category,
          stage: r.stage,
          nominated_sale_price: parseFloat(r.nominated_sale_price),
          sale_price: ['sold', 'payout_issued'].includes(r.stage) ? parseFloat(r.sale_price) : null,
          marketplace_id: r.marketplace_id.trim() || null,
        }))
      };
      const { data } = await axios.post(`${API}/admin/reentry/bulk`, payload, { withCredentials: true });
      setResults(data.results);
      if (data.failed === 0) {
        toast.success(`${data.created} record${data.created === 1 ? '' : 's'} re-entered — no SMS was sent`);
        setRows([emptyRow()]);
      } else {
        toast.warning(`${data.created} created, ${data.failed} failed — check row errors below`);
        setRows(prev => prev.filter((_, i) => !data.results[i]?.success));
        setResults(data.results.filter(r => !r.success));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally {
      setSubmitting(false);
    }
  };

  const isSold = (r) => ['sold', 'payout_issued'].includes(r.stage);

  return (
    <div className="p-6 md:p-8 max-w-6xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#474747] flex items-center gap-2" data-testid="reentry-title">
          <DatabaseZap className="w-6 h-6" /> Bulk Data Re-Entry
        </h1>
        <p className="text-sm text-gray-500">Rebuild lost applications and items fast. Creates sellers, applications, items and payouts at any stage. <span className="font-semibold text-[#474747]">No SMS is ever sent from this tool.</span></p>
        <div className="flex flex-wrap gap-2 mt-3">
          <Button size="sm" variant="outline" onClick={downloadTemplate} data-testid="download-template-button">
            <FileDown className="w-4 h-4 mr-1" /> Download CSV Template
          </Button>
          <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} data-testid="upload-csv-button">
            <FileUp className="w-4 h-4 mr-1" /> Upload CSV
          </Button>
          <input ref={fileInputRef} type="file" accept=".csv,text/csv" className="hidden" data-testid="csv-file-input"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleCsvUpload(f); e.target.value = ''; }} />
        </div>
      </div>

      <div className="space-y-4">
        {rows.map((r, idx) => (
          <div key={r._rid || `row-${idx}`} className="bg-white rounded-xl border border-[#E9E9E9] p-4" data-testid={`reentry-row-${idx}`}>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Record {idx + 1}</p>
              {rows.length > 1 && (
                <button onClick={() => setRows(prev => prev.filter((_, i) => i !== idx))} className="text-gray-400 hover:text-red-500" data-testid={`remove-row-${idx}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
              <Input placeholder="Seller name *" value={r.seller_name} onChange={(e) => setField(idx, 'seller_name', e.target.value)} data-testid={`row-${idx}-seller-name`} />
              <Input placeholder="Seller phone" value={r.seller_phone} onChange={(e) => setField(idx, 'seller_phone', e.target.value)} data-testid={`row-${idx}-seller-phone`} />
              <Input placeholder="Seller email" value={r.seller_email} onChange={(e) => setField(idx, 'seller_email', e.target.value)} data-testid={`row-${idx}-seller-email`} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
              <Input placeholder="Item title *" value={r.item_title} onChange={(e) => setField(idx, 'item_title', e.target.value)} data-testid={`row-${idx}-item-title`} />
              <Select value={r.category} onValueChange={(v) => setField(idx, 'category', v)}>
                <SelectTrigger data-testid={`row-${idx}-category`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={r.stage} onValueChange={(v) => setField(idx, 'stage', v)}>
                <SelectTrigger data-testid={`row-${idx}-stage`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STAGES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Input type="number" min="0" placeholder="Nominated sale price ($) *" value={r.nominated_sale_price} onChange={(e) => setField(idx, 'nominated_sale_price', e.target.value)} data-testid={`row-${idx}-nominated-price`} />
              {isSold(r) && (
                <Input type="number" min="0" placeholder="Final sale price ($) *" value={r.sale_price} onChange={(e) => setField(idx, 'sale_price', e.target.value)} data-testid={`row-${idx}-sale-price`} />
              )}
              <Input placeholder="Marketplace ID (optional)" value={r.marketplace_id} onChange={(e) => setField(idx, 'marketplace_id', e.target.value)} data-testid={`row-${idx}-marketplace-id`} />
            </div>
            {results && results.find(res => res.row === idx && !res.success) && (
              <p className="mt-2 text-xs text-red-600 flex items-center gap-1" data-testid={`row-${idx}-error`}>
                <XCircle className="w-3.5 h-3.5" /> {results.find(res => res.row === idx).error}
              </p>
            )}
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-3 mt-4">
        <Button variant="outline" onClick={() => setRows(prev => [...prev, emptyRow()])} data-testid="add-row-button">
          <Plus className="w-4 h-4 mr-1" /> Add Record
        </Button>
        <Button onClick={submit} disabled={submitting} className="bg-[#C6E76C] hover:bg-[#b5d65f] text-[#1E2117] font-semibold" data-testid="reentry-submit-button">
          <Upload className="w-4 h-4 mr-1" /> {submitting ? 'Importing…' : `Import ${rows.length} Record${rows.length === 1 ? '' : 's'}`}
        </Button>
      </div>

      {results && results.every(r => r.success) && results.length > 0 && (
        <div className="mt-4 p-4 rounded-lg bg-[#F7FBEC] border border-[#C6E76C]" data-testid="reentry-success-panel">
          <p className="text-sm font-semibold text-[#3D4237] flex items-center gap-1.5">
            <CheckCircle2 className="w-4 h-4" /> All records imported successfully
          </p>
          <p className="text-xs text-gray-600 mt-1">References: {results.map(r => r.reference_code).join(', ')}</p>
        </div>
      )}
    </div>
  );
}
